/**
 * Header notification bell.
 *
 * Pairs with snippets/header-notifications.liquid. Notifications themselves come
 * from the `notification` metaobject; this element handles open/close, sorts the
 * list newest-first by each item's data-date, and tracks read state per-browser
 * in localStorage so the badge behaves like an app notification counter.
 * Opening the panel does NOT mark anything read — the badge persists until the
 * customer explicitly hits "Mark all as read" in the dropdown, so the counter
 * keeps drawing attention across visits.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ew:notifications:seen';

  function readSeen() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeSeen(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      /* private browsing / storage disabled — badge simply stays session-only */
    }
  }

  class NotificationBell extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.notification-bell__button');
      this.panel = this.querySelector('.notification-bell__panel');
      this.badge = this.querySelector('.notification-bell__count');
      this.clearButton = this.querySelector('.notification-bell__clear');
      if (!this.button || !this.panel) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.sortNewestFirst();
      this.applyUnreadState();

      this.button.addEventListener('click', () => {
        this.classList.contains('is-open') ? this.close() : this.open();
      });

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => this.markAllRead());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    get items() {
      return Array.from(this.querySelectorAll('.notification-bell__item'));
    }

    sortNewestFirst() {
      var list = this.querySelector('.notification-bell__list');
      if (!list) return;

      this.items
        .sort(function (a, b) {
          return (b.dataset.date || '').localeCompare(a.dataset.date || '');
        })
        .forEach(function (item) {
          list.appendChild(item);
        });
    }

    applyUnreadState() {
      var seen = readSeen();
      var unread = 0;

      this.items.forEach(function (item) {
        var isUnread = seen.indexOf(item.dataset.handle) === -1;
        item.classList.toggle('is-unread', isUnread);
        if (isUnread) unread += 1;
      });

      if (this.badge) {
        this.badge.textContent = unread > 9 ? '9+' : String(unread);
        this.badge.hidden = unread === 0;
      }

      if (this.clearButton) {
        this.clearButton.hidden = unread === 0;
      }
    }

    open() {
      // On mobile the panel is position:fixed, so anchor it just below the bell.
      var rect = this.button.getBoundingClientRect();
      this.panel.style.setProperty('--notification-panel-top', rect.bottom + 10 + 'px');

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    markAllRead() {
      var handles = this.items
        .map(function (item) {
          return item.dataset.handle;
        })
        .filter(Boolean);

      var seen = readSeen();
      handles.forEach(function (handle) {
        if (seen.indexOf(handle) === -1) seen.push(handle);
      });

      // Drop handles for notifications that no longer exist so the key can't grow forever.
      writeSeen(
        seen.filter(function (handle) {
          return handles.indexOf(handle) !== -1;
        })
      );

      this.applyUnreadState();
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.button.focus();
      }
    }
  }

  if (!customElements.get('notification-bell')) {
    customElements.define('notification-bell', NotificationBell);
  }
})();
